import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';
import Modal from '../components/Modal';

interface ApiKey {
  id: number;
  keyName: string;
  apiKey: string;
  fullApiKey?: string; // Store full key temporarily (only available after create/regenerate)
  keyType: 'admin' | 'user';
  permissions: Record<string, Record<string, boolean>>;
  isActive: boolean;
  lastUsed: string | null;
  createdAt: string;
  expiresAt: string | null;
  user: {
    id: number;
    username: string;
    fullName: string;
    role: string;
  };
}

const OrderApiKeys: React.FC = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'keys' | 'docs'>('keys');

  // Form state
  const [formData, setFormData] = useState({
    keyName: '',
    permissions: {
      orders: {
        read: false,
        create: false,
        update: false,
        delete: false,
        cancel: false,
        bulk_cancel: false,
        refill: false,
        bulk_refill: false,
      }
    },
    expiryDays: 365,
  });

  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const availablePermissions = [
    { key: 'read', label: '📖 Đọc đơn hàng', color: '#3b82f6' },
    { key: 'create', label: '➕ Tạo đơn hàng', color: '#10b981' },
    { key: 'update', label: '✏️ Cập nhật đơn hàng', color: '#f59e0b' },
    { key: 'delete', label: '🗑️ Xóa đơn hàng', color: '#ef4444' },
    { key: 'cancel', label: '❌ Hủy đơn hàng', color: '#ec4899' },
    { key: 'bulk_cancel', label: '🔄 Hủy hàng loạt', color: '#8b5cf6' },
    { key: 'refill', label: '♻️ Bảo hành', color: '#059669' },
    { key: 'bulk_refill', label: '♻️ Bảo hành hàng loạt', color: '#047857' },
  ];

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiRequest('/api/order-api-keys');

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.data?.orderApiKeys || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch API keys');
        setApiKeys([]);
      }
    } catch (err) {
      setError('Network error');
      setApiKeys([]);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission('order_api_keys', 'read')) {
      navigate('/dashboard');
      return;
    }
    fetchApiKeys();
  }, [hasPermission, navigate]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 3000);
  };

  const handleCreateKey = async () => {
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + formData.expiryDays);

      const response = await apiRequest('/api/order-api-keys', {
        method: 'POST',
        body: JSON.stringify({
          keyName: formData.keyName,
          permissions: formData.permissions,
          expiresAt: expiresAt.toISOString(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const fullKey = data.data.apiKey;
        setNewApiKey(fullKey);

        // Update the apiKeys state to include the full key for the newly created key
        setApiKeys(prev => {
          const newKey = data.data;
          return [{ ...newKey, fullApiKey: fullKey }, ...prev];
        });

        showNotification('success', 'API Key created successfully');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Failed to create API key');
      }
    } catch (err) {
      showNotification('error', 'Network error');
      console.error('Create error:', err);
    }
  };

  const handleUpdateKey = async () => {
    if (!editingKey) return;

    try {
      const response = await apiRequest(`/api/order-api-keys/${editingKey.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          keyName: formData.keyName,
          permissions: formData.permissions,
          isActive: editingKey.isActive,
        }),
      });

      if (response.ok) {
        showNotification('success', 'API Key updated successfully');
        setEditingKey(null);
        fetchApiKeys();
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Failed to update API key');
      }
    } catch (err) {
      showNotification('error', 'Network error');
      console.error('Update error:', err);
    }
  };

  const handleDeleteKey = async (id: number) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;

    try {
      const response = await apiRequest(`/api/order-api-keys/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showNotification('success', 'API Key revoked successfully');
        fetchApiKeys();
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Failed to revoke API key');
      }
    } catch (err) {
      showNotification('error', 'Network error');
      console.error('Delete error:', err);
    }
  };

  const handleRegenerateKey = async (id: number) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will stop working.')) return;

    try {
      const response = await apiRequest(`/api/order-api-keys/${id}/regenerate`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        const fullKey = data.data.apiKey;
        setNewApiKey(fullKey);

        // Update the apiKeys state to include the full key for the regenerated key
        setApiKeys(prev => prev.map(key =>
          key.id === id ? { ...data.data, fullApiKey: fullKey } : key
        ));

        showNotification('success', 'API Key regenerated successfully');
        setShowCreateModal(true); // Show modal with new key
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Failed to regenerate API key');
      }
    } catch (err) {
      showNotification('error', 'Network error');
      console.error('Regenerate error:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotification('success', 'Copied to clipboard');
  };

  const openCreateModal = () => {
    setFormData({
      keyName: '',
      permissions: {
        orders: {
          read: false,
          create: false,
          update: false,
          delete: false,
          cancel: false,
          bulk_cancel: false,
          refill: false,
          bulk_refill: false,
        }
      },
      expiryDays: 365,
    });
    setNewApiKey('');
    setShowCreateModal(true);
  };

  const openEditModal = (key: ApiKey) => {
    setEditingKey(key);

    // Extract permissions with proper type
    const orderPerms = key.permissions.orders || {};

    setFormData({
      keyName: key.keyName,
      permissions: {
        orders: {
          read: orderPerms.read || false,
          create: orderPerms.create || false,
          update: orderPerms.update || false,
          delete: orderPerms.delete || false,
          cancel: orderPerms.cancel || false,
          bulk_cancel: orderPerms.bulk_cancel || false,
          refill: orderPerms.refill || false,
          bulk_refill: orderPerms.bulk_refill || false,
        }
      },
      expiryDays: 365,
    });
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        orders: {
          ...prev.permissions.orders,
          [permission]: checked,
        }
      }
    }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>🔑 Order API Keys</h1>
          {activeTab === 'keys' && hasPermission('order_api_keys', 'create') && (
            <button
              onClick={openCreateModal}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              ➕ Create API Key
            </button>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <button
            onClick={() => setActiveTab('keys')}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: activeTab === 'keys' ? '#3b82f6' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'keys' ? '2px solid #3b82f6' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'keys' ? 'bold' : 'normal',
              marginBottom: '-2px',
            }}
          >
            🔑 API Keys Management
          </button>
          <button
            onClick={() => setActiveTab('docs')}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: activeTab === 'docs' ? '#3b82f6' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'docs' ? '2px solid #3b82f6' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'docs' ? 'bold' : 'normal',
              marginBottom: '-2px',
            }}
          >
            📚 API Documentation
          </button>
        </div>

        {/* Tab Content: API Keys Management */}
        {activeTab === 'keys' && (
          <>
            {/* Notification */}
            {notification.show && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
                color: notification.type === 'success' ? '#065f46' : '#991b1b',
                borderRadius: '5px',
                border: `1px solid ${notification.type === 'success' ? '#10b981' : '#ef4444'}`,
              }}>
                {notification.message}
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderRadius: '5px',
                border: '1px solid #ef4444',
              }}>
                {error}
              </div>
            )}

            {/* Loading */}
            {loading && <div>Loading...</div>}

        {/* API Keys Table */}
        {!loading && apiKeys && apiKeys.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>API Key</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Permissions</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>User</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Last Used</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Expires</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>{key.keyName}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <code style={{
                          padding: '4px 8px',
                          backgroundColor: key.fullApiKey ? '#dcfce7' : '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {key.fullApiKey || key.apiKey}
                        </code>
                        {key.fullApiKey ? (
                          <button
                            onClick={() => copyToClipboard(key.fullApiKey!)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            📋 Copy
                          </button>
                        ) : (
                          <span style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            fontStyle: 'italic'
                          }}>
                            (Regenerate để lấy key mới)
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Object.entries(key.permissions.orders || {}).map(([perm, enabled]) =>
                          enabled && (
                            <span key={perm} style={{
                              padding: '2px 8px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              borderRadius: '12px',
                              fontSize: '11px',
                            }}>
                              {perm}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{key.user.fullName}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>@{key.user.username}</div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: key.isActive && !isExpired(key.expiresAt) ? '#d1fae5' : '#fee2e2',
                        color: key.isActive && !isExpired(key.expiresAt) ? '#065f46' : '#991b1b',
                      }}>
                        {isExpired(key.expiresAt) ? '⏰ Expired' : key.isActive ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                      {formatDate(key.lastUsed)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                      {formatDate(key.expiresAt)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {hasPermission('order_api_keys', 'update') && (
                          <>
                            <button
                              onClick={() => openEditModal(key)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleRegenerateKey(key.id)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              🔄 Regenerate
                            </button>
                          </>
                        )}
                        {hasPermission('order_api_keys', 'delete') && (
                          <button
                            onClick={() => handleDeleteKey(key.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            🗑️ Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

            {/* No data */}
            {!loading && (!apiKeys || apiKeys.length === 0) && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <p style={{ fontSize: '18px', color: '#6b7280' }}>No API keys found</p>
              </div>
            )}
          </>
        )}

        {/* Tab Content: API Documentation */}
        {activeTab === 'docs' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginTop: 0, color: '#1f2937' }}>📚 Order API Documentation</h2>

            {/* Quick Start */}
            <section style={{ marginBottom: '40px' }}>
              <h3 style={{ color: '#3b82f6', borderBottom: '2px solid #3b82f6', paddingBottom: '10px' }}>
                🚀 Quick Start
              </h3>
              <p>Use your Order API Key to authenticate requests to order management endpoints.</p>

              <div style={{
                backgroundColor: '#f3f4f6',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '15px'
              }}>
                <strong>Authentication Header:</strong>
                <pre style={{
                  backgroundColor: '#1f2937',
                  color: '#10b981',
                  padding: '15px',
                  borderRadius: '5px',
                  overflow: 'auto',
                  marginTop: '10px'
                }}>
{`X-API-Key: smm_order_your_api_key_here`}
                </pre>
              </div>
            </section>

            {/* Available Endpoints */}
            <section style={{ marginBottom: '40px' }}>
              <h3 style={{ color: '#3b82f6', borderBottom: '2px solid #3b82f6', paddingBottom: '10px' }}>
                🔌 Available Endpoints
              </h3>

              {/* Endpoint 1: Get Order History */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <span style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>GET</span>
                  <code style={{ fontSize: '16px', fontWeight: 'bold' }}>/api/orders/history/bulk</code>
                </div>

                <p><strong>Description:</strong> Get order history by multiple order IDs</p>
                <p><strong>Permission Required:</strong> <code>orders.read</code></p>

                <div style={{ marginTop: '15px' }}>
                  <strong>Query Parameters:</strong>
                  <ul style={{ marginTop: '10px' }}>
                    <li><code>ids</code> (required) - Comma-separated order IDs (max 1000)</li>
                  </ul>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Request:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#10b981',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px'
                  }}>
{`curl -X GET "http://your-domain.com/api/orders/history/bulk?ids=1,2,3,4,5" \\
  -H "X-API-Key: smm_order_your_api_key_here"`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Response:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "success": true,
  "data": {
    "orders": [
      {
        "id_smm": 1,
        "order_id": "ORD-001",
        "service_id": 1490,
        "link": "https://facebook.com/post/123",
        "quantity": 1000,
        "status": "Completed",
        "created_at": "2025-11-05T10:00:00Z"
      }
    ],
    "total": 1
  }
}`}
                  </pre>
                </div>
              </div>

              {/* Endpoint 2: Cancel Single Order */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <span style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>POST</span>
                  <code style={{ fontSize: '16px', fontWeight: 'bold' }}>/api/orders/:id/cancel-provider</code>
                </div>

                <p><strong>Description:</strong> Cancel a single order at the provider</p>
                <p><strong>Permission Required:</strong> <code>orders.cancel</code></p>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Request:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#10b981',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px'
                  }}>
{`curl -X POST "http://your-domain.com/api/orders/123/cancel-provider" \\
  -H "X-API-Key: smm_order_your_api_key_here"`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Response:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "success": true,
  "message": "Order cancelled successfully",
  "data": {
    "id_smm": 123,
    "status": "Cancelled"
  }
}`}
                  </pre>
                </div>
              </div>

              {/* Endpoint 3: Bulk Cancel Orders */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <span style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>POST</span>
                  <code style={{ fontSize: '16px', fontWeight: 'bold' }}>/api/orders/bulk-cancel-provider</code>
                </div>

                <p><strong>Description:</strong> Cancel multiple orders at the provider</p>
                <p><strong>Permission Required:</strong> <code>orders.bulk_cancel</code></p>

                <div style={{ marginTop: '15px' }}>
                  <strong>Request Body:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "orderIds": [1, 2, 3, 4, 5]
}`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Request:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#10b981',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px'
                  }}>
{`curl -X POST "http://your-domain.com/api/orders/bulk-cancel-provider" \\
  -H "X-API-Key: smm_order_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"orderIds": [1, 2, 3, 4, 5]}'`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Response:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "success": true,
  "message": "Bulk cancellation completed",
  "data": {
    "total": 5,
    "successful": 4,
    "failed": 1,
    "results": [...]
  }
}`}
                  </pre>
                </div>
              </div>

              {/* Endpoint 4: Single Refill Order */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <span style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>POST</span>
                  <code style={{ fontSize: '16px', fontWeight: 'bold' }}>/api/orders/:id/refill-provider</code>
                </div>

                <p><strong>Description:</strong> Request warranty/refill for a single order at the provider (currently only sabo supported)</p>
                <p><strong>Permission Required:</strong> <code>orders.refill</code> or <code>orders.update</code></p>
                <p><strong>Note:</strong> Only completed orders can request warranty</p>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Request:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#10b981',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px'
                  }}>
{`curl -X POST "http://your-domain.com/api/orders/123/refill-provider" \\
  -H "X-API-Key: smm_order_your_api_key_here"`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Response:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "success": true,
  "message": "Warranty request submitted successfully."
}`}
                  </pre>
                </div>
              </div>

              {/* Endpoint 5: Bulk Refill Orders */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <span style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>POST</span>
                  <code style={{ fontSize: '16px', fontWeight: 'bold' }}>/api/orders/bulk-refill-provider</code>
                </div>

                <p><strong>Description:</strong> Request warranty/refill for multiple orders at the provider</p>
                <p><strong>Permission Required:</strong> <code>orders.bulk_refill</code> or <code>orders.update</code></p>
                <p><strong>Note:</strong> Maximum 100 orders per request. Only completed orders can request warranty.</p>

                <div style={{ marginTop: '15px' }}>
                  <strong>Request Body:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "orderIds": [1, 2, 3, 4, 5]
}`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Request:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#10b981',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px'
                  }}>
{`curl -X POST "http://your-domain.com/api/orders/bulk-refill-provider" \\
  -H "X-API-Key: smm_order_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"orderIds": [1, 2, 3, 4, 5]}'`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <strong>Example Response:</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#60a5fa',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
{`{
  "success": true,
  "message": "Completed: 4 warranty requests successful, 1 failed.",
  "results": {
    "successful": [
      { "orderId": 1 },
      { "orderId": 2 },
      { "orderId": 3 },
      { "orderId": 5 }
    ],
    "failed": [
      { "orderId": 4, "error": "This order does not support automatic warranty." }
    ]
  }
}`}
                  </pre>
                </div>
              </div>
            </section>

            {/* Error Handling */}
            <section style={{ marginBottom: '40px' }}>
              <h3 style={{ color: '#3b82f6', borderBottom: '2px solid #3b82f6', paddingBottom: '10px' }}>
                ⚠️ Error Handling
              </h3>

              <div style={{ marginTop: '15px' }}>
                <strong>Common Error Responses:</strong>

                <div style={{ marginTop: '15px' }}>
                  <p><strong>401 Unauthorized:</strong> Invalid or missing API key</p>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#ef4444',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    fontSize: '12px'
                  }}>
{`{
  "error": "Invalid API key"
}`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <p><strong>403 Forbidden:</strong> Insufficient permissions</p>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#ef4444',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    fontSize: '12px'
                  }}>
{`{
  "error": "Insufficient permissions"
}`}
                  </pre>
                </div>

                <div style={{ marginTop: '15px' }}>
                  <p><strong>404 Not Found:</strong> Order not found</p>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#ef4444',
                    padding: '15px',
                    borderRadius: '5px',
                    overflow: 'auto',
                    fontSize: '12px'
                  }}>
{`{
  "error": "Order not found"
}`}
                  </pre>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <section>
              <h3 style={{ color: '#3b82f6', borderBottom: '2px solid #3b82f6', paddingBottom: '10px' }}>
                💡 Best Practices
              </h3>

              <ul style={{ lineHeight: '1.8' }}>
                <li>🔒 <strong>Keep your API key secure</strong> - Never commit it to version control</li>
                <li>🔄 <strong>Rotate keys regularly</strong> - Use the regenerate feature periodically</li>
                <li>⏰ <strong>Set expiration dates</strong> - Configure appropriate expiry for your keys</li>
                <li>🎯 <strong>Use minimal permissions</strong> - Only grant permissions you need</li>
                <li>📊 <strong>Monitor usage</strong> - Check "Last Used" timestamps regularly</li>
                <li>🚫 <strong>Revoke unused keys</strong> - Delete keys that are no longer needed</li>
                <li>⚡ <strong>Handle rate limits</strong> - Implement retry logic with exponential backoff</li>
                <li>🔍 <strong>Log API calls</strong> - Keep track of your API usage for debugging</li>
              </ul>
            </section>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <Modal
            isOpen={showCreateModal}
            title="Create New API Key"
            onClose={() => setShowCreateModal(false)}
          >
            {newApiKey ? (
              <div>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  marginBottom: '20px',
                }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>⚠️ Save this API key securely!</p>
                  <p style={{ marginBottom: '10px' }}>It will not be shown again.</p>
                  <code style={{
                    display: 'block',
                    padding: '10px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}>
                    {newApiKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newApiKey)}
                    style={{
                      marginTop: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewApiKey('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Key Name *
                  </label>
                  <input
                    type="text"
                    value={formData.keyName}
                    onChange={(e) => setFormData({ ...formData, keyName: e.target.value })}
                    placeholder="e.g., Production API Key"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    Permissions
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {availablePermissions.map((perm) => (
                      <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={formData.permissions.orders[perm.key as keyof typeof formData.permissions.orders]}
                          onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                        />
                        <span style={{ color: perm.color }}>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Expiry (days)
                  </label>
                  <input
                    type="number"
                    value={formData.expiryDays}
                    onChange={(e) => setFormData({ ...formData, expiryDays: parseInt(e.target.value) })}
                    min="1"
                    max="3650"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateKey}
                    disabled={!formData.keyName}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: formData.keyName ? '#3b82f6' : '#d1d5db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: formData.keyName ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            )}
          </Modal>
        )}

        {/* Edit Modal */}
        {editingKey && (
          <Modal
            isOpen={!!editingKey}
            title="Edit API Key"
            onClose={() => setEditingKey(null)}
          >
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Key Name *
                </label>
                <input
                  type="text"
                  value={formData.keyName}
                  onChange={(e) => setFormData({ ...formData, keyName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  Permissions
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {availablePermissions.map((perm) => (
                    <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.orders[perm.key as keyof typeof formData.permissions.orders]}
                        onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                      />
                      <span style={{ color: perm.color }}>{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setEditingKey(null)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateKey}
                  disabled={!formData.keyName}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: formData.keyName ? '#f59e0b' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: formData.keyName ? 'pointer' : 'not-allowed',
                  }}
                >
                  Update
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default OrderApiKeys;

